import React from 'react'
import Head from 'next/head'
import { formatISO9075 } from 'date-fns/formatISO9075'
import { parseISO } from 'date-fns/parseISO'
import type { ArticleHeadFragment } from '../../../.graphql/generated-types'
import { Locale, replaceTldForLocale, siteLocale } from '@marketing-sites/utils'

const domain = process.env['NEXT_PUBLIC_DOMAIN'] ?? 'readingeggs.com.au'

function buildArticleURLForLocale(
  articleSlugForLocale: string,
  locale: Locale,
): string {
  const url = replaceTldForLocale(domain, locale)
  url.pathname = `/articles/${articleSlugForLocale}/`
  return url.toString()
}

export interface ArticleHeadProps {
  data: ArticleHeadFragment
}

export function ArticleHead({
  data: {
    seoDescription,
    seoKeywords,
    isIndexed: maybeIsIndexed,
    name,
    publishedAt,
    imageForArticleMetadata,
    slug,
    slugForEnAU,
    slugForEnCA,
    slugForEnGB,
    slugForEnNZ,
    slugForEnUS,
    slugForEnZA,
    sys,
  },
}: ArticleHeadProps) {
  const title = name ?? ''
  const description = seoDescription ?? ''
  const isIndexed = maybeIsIndexed ?? false
  const keywords = seoKeywords ?? [
    'childhood education',
    'reading',
    'writing',
    'education',
  ]
  const publishDateAsString = publishedAt ?? ''
  let publishDateParsed = new Date()
  try {
    publishDateParsed = parseISO(publishDateAsString as string)
  } catch {
    // Do nothing
  }
  const publishDateFormatted = formatISO9075(publishDateParsed)
  const updatedDate = 'publishedAt' in sys ? (sys.publishedAt as string) : false
  const updatedDateFormatted = formatISO9075(updatedDate || publishDateParsed)
  const imageURL = imageForArticleMetadata?.imageAsset?.url ?? ''
  const articleSlug = slug ?? ''
  const localizedVersions = [
    {
      locale: Locale.au,
      href: buildArticleURLForLocale(slugForEnAU ?? '', Locale.au),
    },
    {
      locale: Locale.us,
      href: buildArticleURLForLocale(slugForEnUS ?? '', Locale.us),
    },
    {
      locale: Locale.ca,
      href: buildArticleURLForLocale(slugForEnCA ?? '', Locale.ca),
    },
    {
      locale: Locale.uk,
      href: buildArticleURLForLocale(slugForEnGB ?? '', Locale.uk),
    },
    {
      locale: Locale.nz,
      href: buildArticleURLForLocale(slugForEnNZ ?? '', Locale.nz),
    },
    {
      locale: Locale.za,
      href: buildArticleURLForLocale(slugForEnZA ?? '', Locale.za),
    },
  ]
  return (
    <Head>
      <title>{title}</title>
      <meta
        name="robots"
        content={isIndexed ? 'index,follow' : 'noindex,nofollow'}
      />
      <meta name="description" content={description} key="description" />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(',')} />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={imageURL} />
      <meta
        property="og:url"
        content={`https://${domain}/articles/${articleSlug}/`}
      />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={buildArticleURLForLocale(slugForEnUS ?? '', Locale.us)}
      />
      {localizedVersions.map(({ locale, href }) => (
        <link key={locale} rel="alternate" hrefLang={locale} href={href} />
      ))}
      <link
        rel="canonical"
        href={buildArticleURLForLocale(slug ?? '', siteLocale)}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `        {
          "@context": "http://schema.org",
          "@type": "Article",
          "headline": "${title}",
          "description": "${description}",
          "url": "https://${domain}/articles/${articleSlug}/",
          "mainEntityOfPage": "https://${domain}/articles/${articleSlug}/",
          "datePublished": "${publishDateFormatted}",
          "dateModified": "${updatedDateFormatted}",
          "author": {
              "@type": "Organization",
              "name": "Reading Eggs"
          },

          "thumbnailUrl": "${imageURL}",
          "image": [
              "${imageURL}"
          ],

          "publisher": {
              "@type": "Organization",
              "name": "Reading Eggs",
              "logo": {
                  "@type": "ImageObject",
                  "url": "https://${domain}/images/fb-thumb-2019_200x200.png",
                  "width": 200,
                  "height": 200
              }
          }
        }`,
        }}
      />
    </Head>
  )
}
